import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigationAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import {
  AUTH,
  AUTH_VERIFY_USER,
  ErrorMessages,
  LOGIN_SOURCE,
  MANAGE_DEVICES,
  USER_STATUS
} from '@smarsh-cyber/shared/constants';
import {
  AppInit,
  AuthToken,
  ErrorModel,
  FactorsModel,
  GlobalState
} from '@smarsh-cyber/shared/models';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  delay,
  exhaustMap,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { AuthFacadeService } from '../auth/+auth_state/auth.facade';
import { WindowsService } from '../shared/services';
import {
  getLoginSource,
  getQueryParams,
  getTargetRoute,
  isCutOverClient,
  isDrupalLogin,
  isNgpLogin,
  setLoginSource
} from '../shared/utils';
import * as AppActions from './app.actions';
import { AppFacadeService } from './app.facade';
import * as appFeature from './app.reducer';
import { UIService } from './ui-services/ui.service';
import { SMARSH_CYBER_APP_SESSION } from './di-token';
import { AppConfigService, AppSessionService } from '../core';

@Injectable()
export class AppEffects {
  appInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.appInit),
      exhaustMap((action) => {
        // TODO: Temp code to be removed post entire Drupal Migration to our login pages
        // do not use refresh token for ag, secure and lpl portals as
        // we are not using cognito flow for these portals.
        // we always want to call drupal get user for these portals.
        // const dontUseToken = !isCutOverClient(action.portalName);
        // UPDATE 30-may-25
        // Enabling token usage but until cookie dependancy is resolved we will not use ngp login
        // UPDATE 16-June-25
        // Enabling condition of using token only in cutover clients
        const token =
          isCutOverClient(action.portalName) ||
          !isDrupalLogin(getLoginSource(action.portalName))
            ? this.windowsService.getFromLocalStorage(
                `${action.portalName}_refreshToken`
              )
            : null;
        return this.uiService
          .getAppInitData(
            action.portalName,
            action.portalUrl,
            action.code,
            token
          )
          .pipe(
            map((response: AppInit) => {
              if (action.code)
                setLoginSource(LOGIN_SOURCE.COGNITO, action.portalName);
              else if (!isNgpLogin(getLoginSource(action.portalName)))
                setLoginSource(LOGIN_SOURCE.DRUPAL, action.portalName);
              return AppActions.appLoadSuccess({
                appInit: {
                  businessUnits: response.businessUnits,
                  user: response.user,
                  loggedInUser: response.loggedInUser,
                  userToken: response.userToken
                }
              });
            }),
            catchError((_error: ErrorModel) =>
              of(AppActions.appLoadFailure({ error: _error, showError: true }))
            )
          );
      })
    )
  );

  appInitNgp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.ngpInit),
      exhaustMap((action) => {
        return this.uiService
          .getTenantAndSubscriptionData(
            action.portalName,
            action.token,
            action.portalUrl,
            null
          )
          .pipe(
            map((response: AppInit) => {
              setLoginSource(LOGIN_SOURCE.NGP, action.portalName);
              return AppActions.appLoadSuccess({
                appInit: {
                  businessUnits: response.businessUnits,
                  user: response.user,
                  loggedInUser: response.loggedInUser,
                  userToken: response.userToken
                }
              });
            }),
            catchError((_error: ErrorModel) => {
              return of(
                AppActions.appLoadFailure({ error: _error, showError: true })
              );
            })
          );
      })
    )
  );

  appInitSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.appLoadSuccess),
        withLatestFrom(this.store.select(appFeature.selectAppState)),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        tap(([action, appState]) => {
          this.config.intialize();
          const portalName = appState?.userToken?.portalName;

          this.windowsService.setToLocalStorage(
            `${portalName}_refreshToken`,
            appState?.userToken?.authToken?.refreshToken
          );

          this.sessionService.initIdle();
          this.checkForAppVersionUpdate();

          // Check if user is impersonating - if yes, dispatch impersonate action
          // Navigation will be handled by impersonate$ effect
          if (this.windowsService.isImpersonating(portalName)) {
            this.store.dispatch(
              AppActions.impersonate({
                impersonate: true,
                userToBeImpersonated:
                  this.windowsService.getImpersonatingUser(portalName)
              })
            );
            // Don't navigate here - impersonate$ effect handles it
            return;
          }

          // Normal login flow - handle navigation
          if (this.router.navigated) {
            this.resolveAndNavigateToTargetRoute(appState);
          } else {
            this.router.events
              .pipe(
                filter((event) => event instanceof NavigationEnd),
                take(1)
              )
              .subscribe(() => this.resolveAndNavigateToTargetRoute(appState));
          }
        })
      ),
    { dispatch: false }
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.refreshToken),
      withLatestFrom(this.store.select(appFeature.selectAppState)),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      exhaustMap(([action, appState]) =>
        this.uiService
          .refreshAuthToken(
            appState?.userToken?.authToken?.refreshToken,
            appState?.userToken.portalName
          )
          .pipe(
            map((response: AuthToken) => {
              this.windowsService.setToLocalStorage(
                `${appState?.userToken.portalName}_refreshToken`,
                response.refreshToken
              );
              return AppActions.refreshTokenSuccess({
                token: response
              });
            }),
            catchError(() => of(AppActions.refreshTokenFailure()))
          )
      )
    )
  );

  appFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.appLoadFailure),
        withLatestFrom(this.store.select(appFeature.selectUserToken)),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        tap(([action]) => {
          if (action.showError)
            this.toasterService.error(ErrorMessages.GENERIC_ERROR);
          this.appFacade.logout();
        })
      ),
    { dispatch: false }
  );

  //TODO: Not used anywhere, check if needed or remove
  factorsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.factorsActions.fetch),
      exhaustMap(() =>
        this.uiService.getFactors().pipe(
          map((response: FactorsModel) =>
            AppActions.factorsActions.fetchSuccess({
              data: response
            })
          ),
          catchError(() => of(AppActions.factorsActions.fetchFailure()))
        )
      )
    )
  );

  factorsAndDevicesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigationAction),
      filter(({ payload }): boolean => {
        return payload.routerState.url.includes(MANAGE_DEVICES);
      }),
      withLatestFrom(this.store.select(appFeature.selectAppState)),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      exhaustMap(([action, appState]) =>
        this.uiService.getFactors().pipe(
          map((response: FactorsModel) =>
            AppActions.factorsActions.fetchSuccess({
              data: response
            })
          ),
          catchError(() => of(AppActions.factorsActions.fetchFailure()))
        )
      )
    )
  );

  impersonate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.impersonate),
        withLatestFrom(this.store.select(appFeature.selectAppState)),
        switchMap(([action, appState]) => {
          // If userToBeImpersonated is provided,
          if (action.userToBeImpersonated || !action.impersonate) {
            this.windowsService.setImpersonatingUser(
              appState.isImpersonating ? action.userToBeImpersonated : null,
              appState.userToken.portalName
            );
            const targetRoute = getTargetRoute(
              appState.userToken.portalName,
              appState.user.position_id,
              appState.user,
              action.navigateToDefault
            );

            const queryParams = getQueryParams();

            this.router
              .navigateByUrl('/', { skipLocationChange: true })
              .then(() => {
                const targetUrl = this.router.createUrlTree([targetRoute], {
                  queryParams: {
                    ...queryParams,
                    portalName: appState?.userToken?.portalName
                  }
                });
                this.router.navigateByUrl(targetUrl);
              });
            return EMPTY;
          }

          // userWithoutPermission -> fetch permissions
          if (action.userWithoutPermission) {
            return this.uiService
              .getUserDetailsWithPermissions(
                action.userWithoutPermission,
                appState.userToken.portalUrl
              )
              .pipe(
                tap((fullUserWithPermissions) => {
                  this.windowsService.setImpersonatingUser(
                    appState.isImpersonating ? fullUserWithPermissions : null,
                    appState.userToken.portalName
                  );
                }),
                map((userWithPermissions) =>
                  AppActions.impersonate({
                    impersonate: action.impersonate,
                    userToBeImpersonated: userWithPermissions,
                    userWithoutPermission: undefined, // prevent loop
                    navigateToDefault: action.navigateToDefault
                  })
                ),
                catchError(() => {
                  this.toasterService.error(ErrorMessages.GENERIC_ERROR);
                  return EMPTY;
                })
              );
          }

          return EMPTY;
        })
      ),
    { dispatch: true }
  );

  ngpLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.ngpLogout),
        withLatestFrom(this.store.select(appFeature.selectAppState)),
        map(([_action, state]) => {
          const portalName = state?.userToken?.portalName;
          const portalUrl = state?.userToken?.portalUrl;
          this.authFacade.initLogoutAndRedirect(
            portalName,
            portalUrl,
            _action.redirectAfterLogout
          );
        })
      ),
    { dispatch: false }
  );

  checkForAppVersionUpdate = () => {
    const RELOAD_FLAG_KEY = 'app_version_reload_timestamp';
    const RELOAD_COOLDOWN_MS = 2 * 60 * 1000; // 2 minute
    const now = Date.now();
    const lastReloadTime = Number(localStorage.getItem(RELOAD_FLAG_KEY) || '0');
    const cooldownPassed = now - lastReloadTime > RELOAD_COOLDOWN_MS;

    this.uiService
      .isNewAppVersionAvailable()
      .pipe(delay(100)) // optional: smooth out UI transitions
      .subscribe((newVersionAvailable) => {
        if (newVersionAvailable && cooldownPassed) {
          localStorage.setItem(RELOAD_FLAG_KEY, now.toString());
          window.location.reload();
        } else if (!newVersionAvailable && lastReloadTime) {
          localStorage.removeItem(RELOAD_FLAG_KEY); // Clean up once updated
        }
      });
  };

  private resolveAndNavigateToTargetRoute(appState: GlobalState) {
    const positionId = appState?.user?.position_id;
    const portalName = appState?.userToken?.portalName;
    const user = appState.user;

    let targetRoute = getTargetRoute(portalName, positionId, user);
    let queryParams = getQueryParams(['code']); //code is passed in saml case hence removed after successful login
    if (queryParams.returnUrl) {
      //remainingQueryParams will have other params except returnUrl
      const { returnUrl, ...remainingQueryParams } = queryParams;
      targetRoute = returnUrl;
      queryParams = remainingQueryParams;
    }

    if (appState.user.status === USER_STATUS.IN_ACTIVE) {
      this.toasterService.error(ErrorMessages.USER_INACTIVE);
      this.appFacade.logout();
    } else {
      if (isCutOverClient(portalName)) {
        if (appState.user.status === USER_STATUS.ACTIVE) {
          this.router.navigate([targetRoute], {
            queryParams
          });
        } else {
          if (user.status === USER_STATUS.PENDING) {
            this.toasterService.warning(ErrorMessages.USER_PENDING);
            this.router.navigate([AUTH, AUTH_VERIFY_USER], {
              queryParams
            });
          } else {
            this.toasterService.warning(ErrorMessages.USER_LOGIN_ISSUE);
            this.appFacade.logout();
          }
        }
      } else {
        //no check for non cutover clients
        this.router.navigate([targetRoute], {
          queryParams
        });
      }
    }
  }

  constructor(
    private actions$: Actions,
    private uiService: UIService,
    private windowsService: WindowsService,
    private store: Store<GlobalState>,
    private router: Router,
    private authFacade: AuthFacadeService,
    private appFacade: AppFacadeService,
    private toasterService: ToastrService,
    private config: AppConfigService,
    @Inject(SMARSH_CYBER_APP_SESSION) private sessionService: AppSessionService
  ) {}
}
